import DefaultIyugaLogo from "@assets/images/iyuga.jpg";
import _ from "lodash";
import PropTypes from "prop-types";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import CreatePieChart from "../D3Wheels/createPie";
// sampleData.js (475 KB of demo segments) was ONLY the `chartData` default prop
// below. Every real <WheelComponent> passes chartData explicitly, so the demo
// dataset never reached users yet sat in the bundle. Replaced with a tiny inline
// fallback (see FALLBACK_CHART_DATA) so the 475 KB leaves the bundle.
// Direct import (NOT the ../PortraitMedia barrel): the barrel also pulls in
// PortraitMediaPanel -> MediaSelection -> heic2any, which would drag the whole
// authoring/upload chain into this core wheel renderer. PortraitVideoOverlay
// only depends on React, so importing it directly keeps WheelComponent's import
// graph unchanged for the flag-OFF path.
import PortraitVideoOverlay from "../PortraitMedia/PortraitVideoOverlay";
import {
  SEGMENT_COMPUTED_LABELS_ENABLED,
  WHEEL_SEGMENTS_FIRST_PAINT_ENABLED,
} from "../../constants/featureFlags";
import {
  withComputedChartLabels,
  restoreOriginalTitles,
} from "../../utils/segmentLabels";
// EW-003 u1b: hydrate each segment's top-level `fill` MODEL (createPie's
// wedgeFillProps reads d.data.fill) so the CLOSED WEDGE renders its fill on every
// user-facing surface (Builder canvas, View Wheel, Gallery) — Drawchart.jsx is the
// only OTHER caller and is NOT mounted on those. Mirrors Drawchart.jsx:337-342.
// Flag OFF → hydrateChartFill returns the SAME array reference → byte-identical.
import { SEGMENT_ARC_FILLS_ENABLED } from "../D3Wheels/segmentArcFillsFlag";
import { hydrateChartFill } from "../SegmentBuilder/Shared/SegmentStyling/logic/fillModelFeed";
import wheelPropsEqual from "./wheelPropsEqual";
// EW-003 GIF-hover (tile preview): on hover (web) / focus (mobile/keyboard) swap
// a gif wedge's frozen poster for an animating <img>. Opt-in per surface via the
// gifHoverPreview prop; flag OFF or prop false => no listeners => byte-identical.
import { TILE_GIF_HOVER_ENABLED } from "../D3Wheels/tileGifHoverFlag";
// ISP-9999 Segment-wedge hover motion: on a large browse tile (the same surfaces
// that opt into gifHoverPreview) hovering the wheel eases every wedge outward in a
// subtle radial "breath". Flag OFF or non-tile surface => createPie never adds the
// class/vars => byte-identical.
import { SEGMENT_HOVER_MOTION_ENABLED } from "../D3Wheels/segmentHoverMotionFlag";
// A11y (ISP-4020): guarantees the wheel-svg :focus-visible ring (and the other
// D3 wheel styles) load wherever this component renders — the ViewWheel uses
// WheelComponent directly (not Drawchart, which is the only other scss importer).
import "../D3Wheels/index.scss";

// ─── PORTRAIT LAYER GEOMETRY (ISP-C4) ───────────────────────────────────────
// The three portrait layers (white backing disc, click target, video overlay)
// are ONE circle: 60% of the wheel's diameter, inset 20% from the container's
// top-left corner.
//
// THE BUG THIS FIXES. They used to be written as `calc(${width} * 0.6)` /
// `calc(${width} * 0.2)` — sized off the `width` PROP, whose default is "40vw".
// But the rendered container is sized by `containerDivStyle` at nearly every
// call site (see memoizedContainerStyle: containerDivStyle spreads OVER width),
// and almost no call site passes `width`. The two geometries therefore diverged
// and the backing disc rendered up to 11x too large — measured live on
// web-stg 2026-07-24: 460.8px inside a 70px avatar at a 1920px viewport, and
// 364.8px inside the Share modal's 72px avatar at 1520px. It is invisible
// wherever it lands on white, and glaring on the Share/Invite modal's dark
// scrim, because `z-index:-1` paints it above the stacking-context root's
// background but below all in-flow content.
//
// THE FIX IS THE CLASS, NOT THE THREE INSTANCES. Percentages resolve against
// the real rendered container, so there is one source of truth for one
// geometry and any call site — present or future, however it sizes its
// container — is correct by construction.
//
// EQUIVALENCE PROOF (why this is a no-op wherever the old code was already
// right). For an absolutely-positioned box, `width`, `left`, AND percentage
// MARGINS (including margin-top) all resolve against the containing block's
// WIDTH — here, this component's container. So wherever the container's used
// width equals the `width` prop W — which is every call site that renders
// correctly today — `60%` === `calc(W * 0.6)` and `20%` === `calc(W * 0.2)`
// exactly, for the size and for BOTH offsets. Same pixels, same subpixels.
//
// WHY NOT `height: 60%` / `top: 20%`. Those percentages resolve against the
// container's HEIGHT, which is a genuinely different length here: `height`
// defaults to "78vh" while `width` defaults to "40vw", and call sites such as
// WheelPantry/children/WheelCard.jsx set ONLY a container height. Using them
// would squash the circle into an ellipse and mis-place it vertically on every
// non-square container — a regression on surfaces that are correct today.
// Hence `aspectRatio` for the height and `top: 0` + `marginTop` for the
// vertical inset. `aspect-ratio` is already load-bearing in this exact feature
// area (WheelGallery/PrimaryWheel's PRIMARY_WHEEL_CONTAINER_STYLE and
// ExploreV2/ExploreWheelCard's WheelArtSquare both rely on it).
const PORTRAIT_LAYER_GEOMETRY = {
  position: "absolute",
  width: "60%",
  aspectRatio: "1 / 1",
  top: 0,
  left: "20%",
  marginTop: "20%",
  borderRadius: "50%",
};

// Frozen module constants: the geometry no longer depends on any prop, so these
// are referentially stable for free (they replace three per-render useMemos).
const PORTRAIT_BG_STYLE = Object.freeze({
  ...PORTRAIT_LAYER_GEOMETRY,
  backgroundColor: "white",
  zIndex: -1,
  pointerEvents: "none",
});
const PORTRAIT_CLICK_STYLE = Object.freeze({
  ...PORTRAIT_LAYER_GEOMETRY,
  backgroundColor: "transparent",
  zIndex: 1,
  cursor: "pointer",
});
const PORTRAIT_OVERLAY_STYLE = Object.freeze({
  ...PORTRAIT_LAYER_GEOMETRY,
  zIndex: 2,
  pointerEvents: "none",
});

const WheelComponent = forwardRef(function WheelComponent(
  {
    userData,
    chartData,
    onArcSelected,
    setChartData,
    maxSelectedArcs,
    showLabels,
    containerDivStyle,
    arcThickness,
    margin,
    height = "78vh",
    segmentHighlighting,
    width = "40vw",
    resize,
    onClickPortrait,
    // When true, skip the HTML white backing disc behind the center portrait.
    // Browse tiles already skip it via gifHoverPreview; the Edit-Wheel builder
    // passes this so the disc does not paint a visible white circle over the
    // canvas wallpaper while keeping onClickPortrait (portrait settings) intact.
    suppressPortraitBackingDisc = false,
    // WL-056b (delta-QA 2026-07-13): optional VIDEO center-media overlay for the
    // live v1 wheel. Shape { type:'video', source, poster }. When present (only
    // ViewWheel/MobileView pass it, and only under VIDEO_PORTRAIT_ENABLED) a
    // muted-autoplay-loop video + its ornaments render clipped to the center
    // portrait circle, over the D3 SVG. undefined => nothing new renders =>
    // byte-identical to today. GIF already animates on the D3 <img> path, so it is
    // not routed here (see PortraitVideoOverlay header).
    centerMedia,
    // EW-003 GIF-hover: when true (tile surfaces only), hovering/focusing the wheel
    // animates its gif wedges. Default false => the View-Wheel / builder wheel is
    // untouched (its gifs already animate in the OPEN frame).
    gifHoverPreview = false,
    // ISP-9999 #920hover (collections-hover-grow-REDRIVE-live-frames) — an
    // explicit per-surface escape hatch from the wedge-hover "breath" that
    // `gifHoverPreview` otherwise bundles in below (`enableWedgeMotion`).
    // gifHoverPreview also drives several OTHER "this is a browse tile"
    // behaviors this surface still wants (full-bleed segments, non-interactive,
    // no backing disc, deferred autoplay) — undefined (every existing tile
    // caller) keeps today's gifHoverPreview-derived value untouched; a surface
    // that wants the tile treatment WITHOUT the grow motion (Collections'
    // showcased-wheel cover, which Gordon's #920hover decision bans "grow/
    // motion" from) passes `false` explicitly.
    enableSegmentHoverMotion,
    // ISP-9999 Discover "live ecosystem": an ambient, non-hover trigger for the
    // SAME segment-wedge hover-breath a tile already wires on real hover/focus.
    // Driven by a page-level timer (useDiscoverLiveEcosystem), not the pointer —
    // see the effect below, which calls the identical `setWedgeHoverExpansion`
    // the real hover listener calls (createPie.js applyWedgeHoverMotion). Default
    // false => byte-identical to today on every surface that never passes it.
    ambientActive = false,
    config = {
      isNotDraggable: true,
      segmentHighlighting: segmentHighlighting,
      segmentLableOpacity: 1,
      segmentLableLimit: 25,
      inactiveOpacity: 0.2, // Default inactive opacity added to config
      showTooltips: true,
      centerGap: 0, // Controls the space between center image and segments
    },
  },
  ref
) {
  const parentRef = useRef(null);
  const pieInstanceRef = useRef(null);
  const isViewMode = false;
  const [isPortraitHovered, setIsPortraitHovered] = useState(false);
  // BUG #927h: the optional card-mode video overlay (below) mounts UNDER the D3
  // svg this component owns, so its own onMouseEnter/onMouseLeave never wins
  // elementsFromPoint at the pointer. Hold a ref to it so the card-root hover
  // re-dispatch (triggerHoverEnter/Leave below) can drive its hover-play
  // directly instead of relying on DOM hit-testing.
  const portraitOverlayRef = useRef(null);

  // Memoize config object to prevent unnecessary re-renders
  const memoizedConfig = useMemo(() => config, [config]);

  // ISP-9999: a large browse tile (gifHoverPreview) gets the segment-wedge hover
  // "breath" when the flag is on. gifHoverPreview is the existing "this is a
  // browse tile" opt-in (View-Wheel / builder / mini-avatar wheels never set it),
  // so this reuses that gate exactly and needs no per-card change. The pie reads
  // config.segmentHoverMotion; effectiveConfig injects it without mutating the
  // caller's config. Flag OFF / non-tile => false => byte-identical.
  const enableWedgeMotion =
    enableSegmentHoverMotion === undefined
      ? gifHoverPreview && SEGMENT_HOVER_MOTION_ENABLED
      : enableSegmentHoverMotion;
  const effectiveConfig = useMemo(
    () => ({ ...memoizedConfig, segmentHoverMotion: enableWedgeMotion }),
    [memoizedConfig, enableWedgeMotion]
  );

  // Memoize userData to prevent unnecessary re-renders.
  // userData?.name is a dep so the portrait's alt text (ISP-4018) updates when
  // the wheel name arrives async, instead of being memo-swallowed.
  // ISP-9999: a card surface that opted into gif-hover (gifHoverPreview + the flag)
  // tags userData so createPie's setProfile renders a gif portrait still-first and
  // wires the hover swap. Non-opt-in surfaces pass userData through untouched
  // (byte-identical); the flag is stable per surface so it never re-memoizes.
  const memoizedUserData = useMemo(
    () =>
      userData && gifHoverPreview && TILE_GIF_HOVER_ENABLED
        ? { ...userData, gifHoverPreview: true }
        : userData,
    [userData?.id, userData?.url, userData?.name, gifHoverPreview]
  );

  // Memoize chartData for stability.
  // DL-029: when SEGMENT_COMPUTED_LABELS_ENABLED, route the data through the ONE
  // shared label helper so unnamed segments render "Segment N" by CURRENT wheel
  // order (renumbers on reorder). Flag OFF returns the original array by
  // reference => byte-identical. Original stored titles are never mutated; the
  // write-back below strips any computed label before it can be persisted.
  const memoizedChartData = useMemo(
    () =>
      SEGMENT_COMPUTED_LABELS_ENABLED
        ? withComputedChartLabels(chartData)
        : chartData,
    [
      // Only re-create if data structure changes
      JSON.stringify(chartData),
    ]
  );

  // EW-003 u1b: attach the top-level `fill` MODEL (createPie.wedgeFillProps reads
  // d.data.fill) so the CLOSED WEDGE paints its image/gif/pattern fill on this
  // shared renderer — the ONLY path Builder/View Wheel/Gallery use. Drawchart.jsx
  // does the SAME hydration; it just isn't mounted on any user-facing wheel.
  // Flag OFF → hydrateChartFill returns memoizedChartData by REFERENCE → this memo
  // yields the same array → byte-identical render. Flag ON but a segment carries no
  // fill (legacy) → resolveSegmentFill returns null → that item is returned
  // unchanged → its wedge markup is byte-identical too. Fed to the pie everywhere
  // memoizedChartData used to be (constructor, updateArcs, resize redraw).
  const hydratedChartData = useMemo(
    () => hydrateChartFill(memoizedChartData, SEGMENT_ARC_FILLS_ENABLED),
    [memoizedChartData]
  );

  // Memoize container style
  //
  // gordon-0921-stg-P0-white-circle-behind-the-wheel (4th report): restores
  // display:flex/alignItems/justifyContent:center + containerType:"size",
  // silently dropped by 6d54a9104 (#4287) when that PR's stale, pre-fork
  // branch was squash-merged over this file on 2026-08-20 — see
  // fe-white-disc-staging-patch-ring-container (#5400, stgport of dev
  // Frontend#5398) and af11ff569 ("keep the gallery hub disc circular"),
  // both of which had already landed on DevOps-Staging-prod before #4287's
  // merge overwrote this block. createPie's ring <svg> is a plain in-flow
  // child sized to Math.min(width, height) — default block flow pins it to
  // this container's TOP-LEFT corner whenever the container itself is not
  // square, while the portrait backing disc below centers on the FULL
  // container on both axes. `display:flex` centering makes the ring share
  // that same center point.
  const memoizedContainerStyle = useMemo(
    () => ({
      width,
      position: "relative",
      zIndex: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...containerDivStyle,
      height: containerDivStyle?.height || height,
      // So 60cqmin-based hub geometry (if a caller relies on it) resolves
      // against the same square createPie's ring uses.
      containerType: "size",
    }),
    [width, height, containerDivStyle]
  );

  // Memoize setChartData to prevent unnecessary re-renders.
  // DL-029 write-back guard: the renderer is fed computed labels, so when it
  // writes data back (e.g. an internal drag-reorder) restore the ORIGINAL stored
  // titles (by id) before propagating, so a computed "Segment N" placeholder is
  // never persisted. Flag OFF passes data straight through (byte-identical).
  const memoizedSetChartData = useCallback(
    (data) => {
      setChartData(
        SEGMENT_COMPUTED_LABELS_ENABLED
          ? restoreOriginalTitles(data, chartData)
          : data
      );
    },
    [setChartData, chartData]
  );

  // Memoize onArcSelected callback
  const memoizedOnArcSelected = useCallback(
    (data) => {
      if (onArcSelected) {
        onArcSelected(data);
      }
    },
    [onArcSelected]
  );

  // Resize handler: trailing debounce so a live width animation (builder pane
  // shrink) coalesces dozens of ResizeObserver frames into one resize pass
  // when the container settles instead of rebuilding every frame.
  // ISP-9999 (stgport of Frontend#5599, gordon-0915-selected-segment-effect-
  // prod-parity, structural half): setSvg() is a FULL teardown — it wipes and
  // recreates every group (arcs/icons/labels/portrait/hidden drag layer).
  // Calling it on every settled resize tore down and rebuilt the entire wheel
  // after a sustained container relayout (e.g. the Segment Settings side panel
  // opening/closing). resizeSvg() only updates what a resize actually changes
  // (radii/arcGenerator/svg dimensions and the two transform-bearing wrapper
  // groups' center offset) and leaves the arc/icon/label/portrait DOM nodes in
  // place for updateArcs() below to update via its existing merge/signature-
  // checked paths.
  const resizeDebounceRef = useRef(null);
  const updateSize = useCallback(() => {
    if (resizeDebounceRef.current) {
      clearTimeout(resizeDebounceRef.current);
    }
    resizeDebounceRef.current = setTimeout(() => {
      resizeDebounceRef.current = null;
      if (pieInstanceRef.current) {
        pieInstanceRef.current.resizeSvg();
        pieInstanceRef.current.updateArcs(hydratedChartData);
        // ISP-4137: draw the segment labels on the resize path too. For a
        // resize=true consumer (the Edit-Wheel builder, wheelFitActive) the
        // ResizeObserver's INITIAL fire is what paints the wheel on mount —
        // updateArcs drew the arcs but not the labels, so labels only ever
        // appeared because the old `id: uuidv4()` re-render churn re-ran the D3
        // effect's else-branch (which calls manageLabels). With the memo now
        // stable that churn is gone, so the labels must be drawn here or they
        // never render. manageLabels is idempotent (removes + redraws) and, being
        // debounced + fired only on an actual size change, adds no per-frame
        // churn. Labels now also track a genuine resize — previously they did not.
        pieInstanceRef.current.manageLabels(showLabels);
      }
    }, 50);
  }, [hydratedChartData, showLabels]);

  // Effect to handle resizing using ResizeObserver
  useEffect(() => {
    if (!resize || !parentRef.current) return;
    const observer = new ResizeObserver(updateSize);
    observer.observe(parentRef.current);

    return () => {
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
        resizeDebounceRef.current = null;
      }
      observer.disconnect();
    };
  }, [resize, updateSize]);

  // Effect to initialize and update D3 instance
  useEffect(() => {
    if (!parentRef.current) return;

    if (!pieInstanceRef.current) {
      pieInstanceRef.current = new CreatePieChart(
        parentRef.current,
        hydratedChartData,
        memoizedUserData,
        memoizedSetChartData,
        isViewMode,
        memoizedOnArcSelected,
        maxSelectedArcs,
        () => {}, // Empty hover callback
        arcThickness,
        () => {},
        effectiveConfig,
        margin
      );
      // First-paint fix (WHEEL_SEGMENTS_FIRST_PAINT): the constructor builds the
      // SVG + center portrait but does NOT draw the segment arcs (only
      // updateArcs() does). When resize is enabled the ResizeObserver's initial
      // callback draws them on mount; when resize is false (the Edit Wheel /
      // builder) nothing does, so segments stay invisible until a side-pane
      // interaction re-runs this effect's else branch. Draw them explicitly here
      // so segments render on first paint. Scoped to !resize so every resize=true
      // consumer is untouched (their ResizeObserver already draws — no
      // double-draw). Flag OFF => byte-identical to today.
      if (WHEEL_SEGMENTS_FIRST_PAINT_ENABLED && !resize) {
        pieInstanceRef.current.updateArcs(hydratedChartData);
        pieInstanceRef.current.manageLabels(showLabels);
      }
    } else {
      // Keep the keyboard-nav config in sync on updates (wheelName arrives async
      // after the wheel data loads) and (re)wire the widget listeners. Both are
      // idempotent + no-ops unless config.keyboardNav is set, so non-opt-in
      // usages stay byte-identical.
      if (memoizedConfig?.keyboardNav !== undefined) {
        pieInstanceRef.current.keyboardNav = memoizedConfig.keyboardNav;
      }
      if (memoizedConfig?.wheelName !== undefined) {
        pieInstanceRef.current.wheelName = memoizedConfig.wheelName;
      }
      // ISP-9999: keep the wedge-hover-motion opt-in live across updates. Idempotent;
      // applyWedgeHoverMotion (run by updateArcs below) re-stamps the container class
      // + per-wedge vars, or clears them when this is false. Byte-identical when false.
      pieInstanceRef.current.segmentHoverMotion = enableWedgeMotion;
      // Adaptive segment icons: keep the live showOnSegment in sync when the
      // wheel's `segment_icon` setting changes (the builder control flip). The
      // pie's segmentIconConfig is otherwise only merged at construction, so
      // without this an existing instance would ignore a mid-session change.
      // Guarded on the key being present, so every non-opt-in usage (no
      // segmentIconConfig in config — the flag-OFF default) is byte-identical.
      // updateArcs() below re-runs updateIcons() and picks up the new value.
      if (memoizedConfig?.segmentIconConfig?.showOnSegment !== undefined) {
        pieInstanceRef.current.segmentIconConfig.showOnSegment =
          memoizedConfig.segmentIconConfig.showOnSegment;
      }
      if (pieInstanceRef.current.keyboardNav) {
        pieInstanceRef.current.setupKeyboardNav();
      }
      pieInstanceRef.current.setMode(isViewMode);
      // Keep geometry knobs live — collection covers pass margin:0 /
      // arcThickness:0; without this an already-mounted pie keeps the
      // constructor defaults and leaves a white rim.
      const nextMargin = margin ?? 50;
      const nextThickness = arcThickness || 0;
      const geometryChanged =
        pieInstanceRef.current.margin !== nextMargin ||
        pieInstanceRef.current.arcThickness !== nextThickness;
      if (geometryChanged) {
        pieInstanceRef.current.margin = nextMargin;
        pieInstanceRef.current.arcThickness = nextThickness;
        pieInstanceRef.current.setSvg();
      }
      pieInstanceRef.current.updateArcs(hydratedChartData);
      pieInstanceRef.current.manageLabels(showLabels);
      pieInstanceRef.current.setProfile(memoizedUserData);
    }
  }, [
    hydratedChartData,
    memoizedUserData,
    showLabels,
    memoizedConfig,
    effectiveConfig,
    enableWedgeMotion,
    maxSelectedArcs,
    arcThickness,
    margin,
    memoizedSetChartData,
    memoizedOnArcSelected,
  ]);

  // Tear down keyboard-nav listeners when the wheel unmounts.
  useEffect(() => {
    return () => {
      if (pieInstanceRef.current && pieInstanceRef.current.destroy) {
        pieInstanceRef.current.destroy();
      }
    };
  }, []);

  // EW-003 GIF-hover (tile preview): wire hover (web) / focus (mobile/keyboard)
  // to mount/unmount the animating gif overlay on the pie. Opt-in via
  // gifHoverPreview + the flag; the pie's mount is itself reduced-motion-safe and
  // a no-op when the wheel has no gif wedge. Flag OFF / prop false => no listeners
  // are attached => byte-identical to today. focusin/focusout cover keyboard and
  // touch-focus; pointerenter/leave cover the mouse.
  useEffect(() => {
    const node = parentRef.current;
    if (!node || !gifHoverPreview || !TILE_GIF_HOVER_ENABLED) return undefined;
    const enter = () => {
      pieInstanceRef.current?.mountGifHoverWedges?.();
      pieInstanceRef.current?.animateGifPortrait?.();
    };
    const leave = () => {
      pieInstanceRef.current?.unmountGifHoverWedges?.();
      pieInstanceRef.current?.freezeGifPortrait?.();
    };
    node.addEventListener("pointerenter", enter);
    node.addEventListener("pointerleave", leave);
    node.addEventListener("focusin", enter);
    node.addEventListener("focusout", leave);
    return () => {
      node.removeEventListener("pointerenter", enter);
      node.removeEventListener("pointerleave", leave);
      node.removeEventListener("focusin", enter);
      node.removeEventListener("focusout", leave);
      pieInstanceRef.current?.unmountGifHoverWedges?.();
      pieInstanceRef.current?.freezeGifPortrait?.();
    };
  }, [gifHoverPreview]);

  // ISP-9999 hover-anywhere-on-card: a card shell (ExploreWheelCard, Gallery's
  // WheelCard) is much bigger than this component's own root box, so a real mouse
  // hover over the card's title/byline/chrome never crosses this element and the
  // pointerenter/mouseenter listeners above (and createPie's own
  // "mouseenter.wedgeHover") never fire. Expose a small imperative API so the CARD
  // ROOT can re-fire the SAME native events on this component's root node from its
  // own onMouseEnter/onMouseLeave — every existing gate (gifHoverPreview,
  // TILE_GIF_HOVER_ENABLED, SEGMENT_HOVER_MOTION_ENABLED, prefers-reduced-motion)
  // still lives in the listeners themselves, so this is a pure re-dispatch, not a
  // parallel code path.
  useImperativeHandle(
    ref,
    () => ({
      triggerHoverEnter: () => {
        const node = parentRef.current;
        if (node) {
          node.dispatchEvent(new Event("mouseenter"));
          node.dispatchEvent(new Event("pointerenter"));
        }
        // BUG #927h: reach the (possibly D3-occluded) video overlay directly —
        // a no-op when no card-mode video is mounted.
        portraitOverlayRef.current?.triggerHoverEnter?.();
      },
      triggerHoverLeave: () => {
        const node = parentRef.current;
        if (node) {
          node.dispatchEvent(new Event("mouseleave"));
          node.dispatchEvent(new Event("pointerleave"));
        }
        portraitOverlayRef.current?.triggerHoverLeave?.();
      },
    }),
    []
  );

  // ISP-9999 Discover "live ecosystem": ambient trigger for the wedge-hover
  // "breath". Calls the EXACT SAME instance method createPie's own
  // mouseenter.wedgeHover/mouseleave.wedgeHover listeners call (see
  // applyWedgeHoverMotion in createPie.js) — no duplicate animation code, and
  // no synthetic DOM events to simulate a hover that never happened. Gated on
  // `enableWedgeMotion` (the existing gifHoverPreview + SEGMENT_HOVER_MOTION_ENABLED
  // / enableSegmentHoverMotion opt-in) so a surface that never gets hover motion
  // never gets ambient motion either, and `setWedgeHoverExpansion` itself still
  // honours prefers-reduced-motion as a second, independent guard.
  useEffect(() => {
    if (!enableWedgeMotion) return undefined;
    pieInstanceRef.current?.setWedgeHoverExpansion?.(!!ambientActive);
  }, [ambientActive, enableWedgeMotion]);

  // Memoize portrait click handler
  const memoizedPortraitClickHandler = useCallback(() => {
    if (onClickPortrait) {
      onClickPortrait();
    }
  }, [onClickPortrait]);

  // White background behind the D3 portrait image.
  // Geometry comes from PORTRAIT_LAYER_GEOMETRY (the container), not the `width`
  // prop — see the block comment above that constant for the why + the proof.
  const portraitBgStyle = PORTRAIT_BG_STYLE;

  // Transparent click target above the D3 portrait image
  const portraitClickStyle = PORTRAIT_CLICK_STYLE;

  // WL-056b: positioning box for the optional video-portrait overlay — the SAME
  // circle geometry the existing portrait bg/click layers use (width*0.6 @
  // width*0.2), so the video is clipped exactly to the D3 center portrait. Sits
  // above the D3 svg; pointer-events:none so only the overlay's play/pause button
  // is interactive and every other click still reaches the wheel beneath.
  const showCenterVideo =
    !!centerMedia && centerMedia.type === "video" && !!centerMedia.source;
  const portraitOverlayStyle = PORTRAIT_OVERLAY_STYLE;

  // Custom tooltip style
  const tooltipStyle = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "#333",
      color: "#fff",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      fontFamily: "DM Sans, sans-serif",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      zIndex: 10,
    }),
    []
  );

  // Browse/tile surfaces (gifHoverPreview) paint over a wallpaper or a round
  // cover clip — createPie already composites the centre portrait without the
  // old ISP-4018 white wash (RP-A1.2), and brand-logo fallbacks carry their own
  // white field inside the SVG portrait wrap. The HTML backing disc here only
  // reads as a glaring white circle on those cards; View Wheel / Builder /
  // Share surfaces (gifHoverPreview unset) keep it for dark scrims + legacy parity.
  const showPortraitBackingDisc =
    !gifHoverPreview && !suppressPortraitBackingDisc;

  return (
    <div ref={parentRef} style={memoizedContainerStyle}>
      {showPortraitBackingDisc && (
        <div data-testid="portrait-backing-disc" style={portraitBgStyle} />
      )}
      {showCenterVideo && (
        <div style={portraitOverlayStyle}>
          {/* ISP-C4: the overlay BOX is now container-derived, but
              PortraitVideoOverlay still scales its ornaments (button, caption)
              off the `width` prop — `calc(width * 0.6)`. That stays consistent
              because both surfaces that pass `centerMedia` (pages/ViewWheel
              desktop and its MobileView delegate) pass an explicit width AND
              height and NO containerDivStyle, so the container's used width IS
              this prop. A future centerMedia call site that sizes its container
              instead would need PortraitVideoOverlay's ornament scale moved to
              percentages too (its `chipFont` uses the value in a font-size, so
              a percentage there is not a drop-in — hence out of scope here). */}
          <PortraitVideoOverlay
            ref={portraitOverlayRef}
            source={centerMedia.source}
            poster={centerMedia.poster}
            width={width}
            // BUG #927a/#927c: `gifHoverPreview` is the existing "this is a
            // browse/tile card surface" signal (Discover/Gallery — see the
            // EW-003 GIF-hover comment above); View Wheel/MobileView/the
            // Builder never set it. Reuse it here so a card portrait gets NO
            // center play/pause box, NO "Muted · Loops" badge (interactive
            // false), and a delayed autoplay start instead of an instant one
            // (deferAutoplay true) — while View Wheel keeps its full,
            // instant-autoplay controls unchanged.
            interactive={!gifHoverPreview}
            deferAutoplay={gifHoverPreview}
          />
        </div>
      )}
      {onClickPortrait && (
        <div
          onClick={memoizedPortraitClickHandler}
          onMouseEnter={() => setIsPortraitHovered(true)}
          onMouseLeave={() => setIsPortraitHovered(false)}
          style={portraitClickStyle}
        >
          {isPortraitHovered && (
            <div style={tooltipStyle}>Wheel Portrait</div>
          )}
        </div>
      )}
    </div>
  );
});

WheelComponent.propTypes = {
  userData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    name: PropTypes.string, // optional: portrait alt text (ISP-4018)
  }),
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      pattern: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ).isRequired,
  onArcSelected: PropTypes.func,
  setChartData: PropTypes.func.isRequired,
  maxSelectedArcs: PropTypes.number,
  showLabels: PropTypes.bool,
  containerDivStyle: PropTypes.object,
  arcThickness: PropTypes.number,
  margin: PropTypes.number,
  segmentHighlighting: PropTypes.bool,
  resize: PropTypes.bool,
  // WL-056b optional video center-media overlay (live v1 wheel).
  centerMedia: PropTypes.shape({
    type: PropTypes.string,
    source: PropTypes.string,
    poster: PropTypes.string,
  }),
  // EW-003 GIF-hover opt-in (tile surfaces).
  gifHoverPreview: PropTypes.bool,
  // ISP-9999 #920hover: explicit wedge-hover-motion override, see the prop's
  // own destructuring comment above. undefined (unset) is the meaningful
  // default -- do not default this in defaultProps, or every caller loses the
  // ability to distinguish "no opinion" from "explicitly false".
  enableSegmentHoverMotion: PropTypes.bool,
  // ISP-9999 Discover live-ecosystem ambient trigger.
  ambientActive: PropTypes.bool,
  suppressPortraitBackingDisc: PropTypes.bool,
  // config.centerGap: number (optional) - controls the space between center image and segments
};

// Minimal, valid-shaped fallback used only if a caller renders <WheelComponent>
// without chartData (no such call exists in the app today — every usage passes it).
const FALLBACK_CHART_DATA = [
  { id: 1, color: "#123051", inner_color: "#8BA6C2", activity_tags: [], value: 1 },
];

WheelComponent.defaultProps = {
  chartData: FALLBACK_CHART_DATA,
  userData: {
    id: "1",
    url: DefaultIyugaLogo,
  },
  onArcSelected: () => {},
  maxSelectedArcs: 1,
  showLabels: false,
  containerDivStyle: {},
  arcThickness: -10,
  margin: 50,
  segmentHighlighting: false,
  resize: true,
  gifHoverPreview: false,
  ambientActive: false,
  config: {
    isNotDraggable: true,
    segmentHighlighting: false,
    segmentLableOpacity: 1,
    segmentLableLimit: 25,
    inactiveOpacity: 0.2,
    showTooltips: true,
    centerGap: 0, // Default gap
  },
};

// Use a custom comparator for the memo to better control rerenders.
// Extracted to ./wheelPropsEqual (testable in isolation) + fixed to also detect a
// Segment Fill MODEL change so the wedge re-renders its fill live in the Builder
// (EW-003, flag REACT_APP_SEGMENT_ARC_FILLS). See that file's header for the bug.
export default React.memo(WheelComponent, wheelPropsEqual);
